import React, { useState } from 'react';
import type { DeckContextState } from './types';

/*
 * Beside the deck: hand Orbit something instead of answering.
 *
 * A card asks one question. A deck, a doc, a link or a pasted note usually
 * answers several, and the person on this screen often has one in front of
 * them. Whatever lands here is read the same way the website was, so the cards
 * it answers leave the deck on their own rather than being asked anyway.
 */

type Mode = 'idle' | 'note' | 'link';

export const DeckContext: React.FC<{ ctx: DeckContextState }> = ({ ctx }) => {
  const [mode, setMode] = useState<Mode>('idle');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  const run = async (work: Promise<void>) => {
    setBusy(true);
    try {
      await work;
      setText('');
      setMode('idle');
    } catch {
      // The caller says what went wrong. What was typed stays on screen so it
      // can be sent again without being written twice.
    } finally {
      setBusy(false);
    }
  };

  const take = (file: File | null | undefined) => {
    setOver(false);
    if (!file || busy) return;
    run(ctx.onFile(file));
  };

  const add = () => {
    const value = text.trim();
    if (!value || busy) return;
    if (mode === 'link') {
      run(ctx.onLink(value));
      return;
    }
    run(ctx.onNote(value));
  };

  let dropClass = 'odeck-ctx-drop';
  if (over) dropClass = 'odeck-ctx-drop over';

  let items = `${ctx.items.length} items`;
  if (ctx.items.length === 1) items = '1 item';

  return (
    <div className="odeck-ctx">
      <h3>Add context</h3>
      <p>Anything that helps Orbit understand this company faster: a deck, a doc, a note, a link.</p>

      <label
        className={dropClass}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}
      >
        <b>Drop a file here</b>
        <small>PDF, doc, deck, or image. Cards it answers leave the deck on their own.</small>
        <input type="file" onChange={(e) => take(e.target.files && e.target.files[0])} disabled={busy} />
      </label>

      {mode === 'idle' && (
        <>
          <div className="odeck-ctx-or"><i />or<i /></div>
          <div className="odeck-ctx-ways">
            <button type="button" onClick={() => setMode('note')} disabled={busy}>Paste text or notes</button>
            <button type="button" onClick={() => setMode('link')} disabled={busy}>Add a link: site, doc, or deck</button>
          </div>
        </>
      )}

      {mode === 'note' && (
        <textarea
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Meeting notes, a brief, an email thread. Anything Orbit should know."
          disabled={busy}
        />
      )}
      {mode === 'link' && (
        <input
          type="url"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="https://"
          disabled={busy}
        />
      )}
      {mode !== 'idle' && (
        <div className="odeck-ctx-act">
          <button type="button" className="quiet" onClick={() => { setMode('idle'); setText(''); }} disabled={busy}>Cancel</button>
          <button type="button" className="go" onClick={add} disabled={busy || !text.trim()}>Add to context</button>
        </div>
      )}

      {busy && <p className="odeck-ctx-busy" role="status" aria-live="polite">Reading it now</p>}

      {ctx.items.length > 0 && (
        <div className="odeck-ctx-had">
          <h4>Already added<span>{items}</span></h4>
          <ul>
            {ctx.items.map((item) => (
              <li key={item.key}><i aria-hidden>✓</i><b>{item.label}</b><span>{item.kind}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DeckContext;
